/**
 * 공통코드 상수 (Single Source of Truth)
 *
 * DB (tb_code) → Java (ClubCodeConstants.java) → 이 파일
 * 변경 시 반드시 3곳을 동시에 수정하고 docs/02_REFERENCE/CODE_CONSTANTS.md 업데이트
 *
 * @see docs/02_REFERENCE/CODE_CONSTANTS.md
 * @see core/.../common/constants/ClubCodeConstants.java
 */

// ========================================
// 동호회 기본
// ========================================

export const CLUB_STATUS = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  SUSPENDED: 'SUSPENDED',
  CLOSE_PENDING: 'CLOSE_PENDING', // 임시폐쇄(유예) — 재활성 가능 (B-06)
  CLOSED: 'CLOSED', // 폐쇄 (B-06)
} as const
export type ClubStatus = (typeof CLUB_STATUS)[keyof typeof CLUB_STATUS]
export const CLUB_STATUS_LABEL: Record<string, string> = {
  ACTIVE: '활성', INACTIVE: '비활성', SUSPENDED: '정지',
  CLOSE_PENDING: '임시폐쇄', CLOSED: '폐쇄',
}

export const CLUB_TYPE = {
  WORK: 'WORK',
  SOCIAL_NEW: 'SOCIAL_NEW',
  REGION: 'REGION',
  SCHOOL: 'SCHOOL',
  HOBBY: 'HOBBY', // 4차 발주(통합본 p33): 취미/관심사 신설
  ETC_NEW: 'ETC_NEW',
} as const
export type ClubType = (typeof CLUB_TYPE)[keyof typeof CLUB_TYPE]

export const CLUB_TYPE_LABEL: Record<ClubType, string> = {
  WORK: '직장',
  SOCIAL_NEW: '친목',
  REGION: '지역',
  SCHOOL: '학교',
  HOBBY: '취미/관심사',
  ETC_NEW: '기타',
}

/**
 * 회비 주기 (TB_CLUB.FEE_CYCLE) — 4차발주 C-10(통합본 p38) "월간회비 → 연회비/월회비 순서 선택".
 * 금액은 `monthlyFee` 를 공용으로 쓰고 주기만 이 값으로 구분한다.
 * 3계층(C-07): DB `tb_code` CODE='FEE_CYCLE' / Java `ClubCodeConstants.FEE_CYCLE_*` / Vue 이 상수.
 */
export const FEE_CYCLE = {
  MONTHLY: 'MONTHLY',
  ANNUAL: 'ANNUAL',
} as const
export type FeeCycle = (typeof FEE_CYCLE)[keyof typeof FEE_CYCLE]

export const FEE_CYCLE_LABEL: Record<FeeCycle, string> = {
  MONTHLY: '월회비',
  ANNUAL: '연회비',
}

/** 회비 주기 선택 옵션 — 발주 p38 표기 순서(연회비 → 월회비) */
export const FEE_CYCLE_OPTIONS: Array<{ value: FeeCycle; label: string }> = [
  { value: FEE_CYCLE.ANNUAL, label: FEE_CYCLE_LABEL.ANNUAL },
  { value: FEE_CYCLE.MONTHLY, label: FEE_CYCLE_LABEL.MONTHLY },
]

/**
 * 동호회 성격 체크박스 옵션 (TB_CLUB.CLUB_CHARACTER — 복수선택 문자열 태그).
 *
 * 4차 발주(통합본 p33 / 크리비즈 8.10):
 *   "직장 친목 지역 학교 취미/관심사 기타 등 6개항목으로 수정, 맛집소개 삭제"
 * 모바일 생성·모바일 설정·PC 생성·PC 수정 4개 화면이 이 상수를 공유한다.
 */
export const CLUB_CHARACTER_TAGS = ['직장', '친목', '지역', '학교', '취미/관심사', '기타'] as const
export const CLUB_CHARACTER_OPTIONS = CLUB_CHARACTER_TAGS.map((t) => ({ value: t, label: t }))

/**
 * 동호회 가입 비밀번호 — 4차 발주(통합본 p33) "비밀번호 4자리 숫자로 고정!"
 * 기존 4~6자리 → 숫자 4자리 고정. 모바일 생성·설정, PC 생성·수정 4개 화면 공유.
 */
export const CLUB_PASSWORD_LENGTH = 4
export const CLUB_PASSWORD_PATTERN = /^\d{4}$/
export const CLUB_PASSWORD_HINT = '비밀번호는 숫자 4자리로 설정해주세요.'

export const JOIN_STATUS = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
} as const
export type JoinStatus = (typeof JOIN_STATUS)[keyof typeof JOIN_STATUS]

export const MEMBER_ROLE = {
  PRESIDENT: 'PRESIDENT',
  VICE_PRESIDENT: 'VICE_PRESIDENT', // 부회장 (C-21) — 2026-07-21 통화: 일반회원 권한(타이틀만, 승인·운영권 없음)
  MANAGER: 'MANAGER',
  VICE_MANAGER: 'VICE_MANAGER',
  SPECIAL: 'SPECIAL',
  MEMBER: 'MEMBER',
} as const
export type MemberRole = (typeof MEMBER_ROLE)[keyof typeof MEMBER_ROLE]

export const GENDER = {
  FEMALE: '2',
  MALE: '1',
} as const
export type MemberGender = (typeof GENDER)[keyof typeof GENDER]

export const MEMBER_ROLE_LABEL: Record<MemberRole, string> = {
  PRESIDENT: '회장',
  VICE_PRESIDENT: '부회장',
  MANAGER: '총무',
  VICE_MANAGER: '부총무',
  SPECIAL: '특별회원',
  MEMBER: '일반회원',
}

// ========================================
// 회원
// ========================================

export const MEMBER_STATUS = {
  ACTIVE: 'ACTIVE',
  PENDING: 'PENDING',
  SUSPENDED: 'SUSPENDED',
  WITHDRAWN: 'WITHDRAWN',
} as const
export type MemberStatus = (typeof MEMBER_STATUS)[keyof typeof MEMBER_STATUS]

export const MEMBER_GRADE = {
  REGULAR: '정회원',
  ASSOCIATE: '준회원',
  HONORARY: '명예회원',
} as const
export type MemberGrade = (typeof MEMBER_GRADE)[keyof typeof MEMBER_GRADE]

// ========================================
// 행사
// ========================================

export const EVENT_TYPE = {
  ROUNDING: 'ROUNDING',
  MEETING: 'MEETING',
  AWARD: 'AWARD',
  INTER_CLUB: 'INTER_CLUB',
} as const
export type EventType = (typeof EVENT_TYPE)[keyof typeof EVENT_TYPE]

export const EVENT_TYPE_LABEL: Record<EventType, string> = {
  ROUNDING: '라운딩',
  MEETING: '기타행사', // L-6: 표시 '일반/일반모임' → '기타행사' (코드값 MEETING 유지)
  AWARD: '시상식',
  INTER_CLUB: '타모임대회',
}

// ─── 행사 일정 유형 (STAY_TYPE) · 라운드 세션 (SESSION_TYPE) — 2026-10-01 공통코드 등록(발주 9/30 슬라이드 35-④) ───
// DB tb_code(STAY_TYPE · SESSION_TYPE) → Java ClubCodeConstants.STAY_TYPE_* · SESSION_TYPE_* → 여기(C-07).
// 코드값은 이미 저장된 값 그대로 — STAY_TYPE 은 한글('1일', '1박2일' …).

export const STAY_TYPE = {
  DAY: '1일',
  N1D2: '1박2일',
  N2D3: '2박3일',
  N3D4: '3박4일',
  N4D5: '4박5일',
  N5D6: '5박6일',
  N6D7: '6박7일',
} as const
export type StayType = (typeof STAY_TYPE)[keyof typeof STAY_TYPE]

/** 화면 라벨 (tb_code.ETC) */
export const STAY_TYPE_LABEL: Record<StayType, string> = {
  '1일': '당일',
  '1박2일': '1박 2일',
  '2박3일': '2박 3일',
  '3박4일': '3박 4일',
  '4박5일': '4박 5일',
  '5박6일': '5박 6일',
  '6박7일': '6박 7일',
}

/** 일수 (tb_code.ETC_SUB) */
export const STAY_TYPE_DAYS: Record<StayType, number> = {
  '1일': 1, '1박2일': 2, '2박3일': 3, '3박4일': 4, '4박5일': 5, '5박6일': 6, '6박7일': 7,
}

/** 선택 상자 목록 — 모바일·PC 행사 만들기 공용 */
export const STAY_TYPE_OPTIONS = (Object.values(STAY_TYPE) as StayType[]).map(v => ({ value: v, label: STAY_TYPE_LABEL[v] }))

/** 라운드 세션 — api/event.ts 가 같은 값을 다시 내보낸다(기존 import 유지) */
export const SESSION_TYPE = {
  AM: 'AM',
  PM: 'PM',
  NIGHT: 'NIGHT',
  CUSTOM: 'CUSTOM',
} as const
export type SessionType = (typeof SESSION_TYPE)[keyof typeof SESSION_TYPE]

export const SESSION_TYPE_LABEL: Record<SessionType, string> = {
  AM: '오전',
  PM: '오후',
  NIGHT: '야간',
  CUSTOM: '기타',
}

export const EVENT_STATUS = {
  DRAFT: 'DRAFT',
  REVIEW_PENDING: 'REVIEW_PENDING',
  PRESIDENT_APPROVED: 'PRESIDENT_APPROVED',
  CONFIRMED: 'CONFIRMED',
  PLANNED: 'PLANNED',
  ONGOING: 'ONGOING',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
} as const
export type EventStatus = (typeof EVENT_STATUS)[keyof typeof EVENT_STATUS]

export const EVENT_STATUS_LABEL: Record<EventStatus, string> = {
  DRAFT: '초안',
  REVIEW_PENDING: '검토중',
  PRESIDENT_APPROVED: '회장승인',
  CONFIRMED: '확정',
  PLANNED: '예정',
  ONGOING: '진행중',
  COMPLETED: '완료',
  CANCELLED: '취소',
}

/** 행사 상태 배지 색 — 모바일 행사 상세(EventDetailPage)의 배지 색을 PC 와 공유하기 위해 옮겼다(2026-09-23) */
export const EVENT_STATUS_BADGE_CLASS: Record<EventStatus, string> = {
  DRAFT: 'bg-[#F7F7FA] text-[#666]',
  REVIEW_PENDING: 'bg-[#FFF3CD] text-[#856404]',
  PRESIDENT_APPROVED: 'bg-[#CCE5FF] text-[#004085]',
  CONFIRMED: 'bg-[#E8F5D0] text-[#5C8A1E]',
  PLANNED: 'bg-[#E8F5D0] text-[#5C8A1E]',
  ONGOING: 'bg-[#D4EDDA] text-[#155724]',
  COMPLETED: 'bg-[#E2E3E5] text-[#383D41]',
  CANCELLED: 'bg-[#F8D7DA] text-[#721C24]',
}

/**
 * 자동 조편성 방식 (TEAM_ASSIGN_MODE) — 4차발주 2편 p57 L19 (R-2-57-4)
 *
 * 2026-08-25 사용자 스펙: "평균 / 비슷한 성적기준 / 섞어서 랜덤 … 조건을 선택"
 * 3계층(C-07): DB `tb_code` CODE='TEAM_ASSIGN_MODE'(CODE_SEQ 610~612, 3DB 고정)
 *            / Java `ClubCodeConstants.TEAM_ASSIGN_MODE_*` / Vue 이 상수.
 * 스키마·시드: sql/hotfix/20260825_auto_team_assign.sql
 */
export const TEAM_ASSIGN_MODE = {
  BALANCED: 'BALANCED',
  SIMILAR: 'SIMILAR',
  RANDOM: 'RANDOM',
} as const
export type TeamAssignMode = (typeof TEAM_ASSIGN_MODE)[keyof typeof TEAM_ASSIGN_MODE]

export const TEAM_ASSIGN_MODE_LABEL: Record<TeamAssignMode, string> = {
  BALANCED: '평균 맞추기',
  SIMILAR: '비슷한 성적끼리',
  RANDOM: '섞어서 랜덤',
}

/** 방식별 한 줄 설명 — 총무가 고르기 전에 결과를 예상할 수 있게 */
export const TEAM_ASSIGN_MODE_DESC: Record<TeamAssignMode, string> = {
  BALANCED: '조마다 평균 실력이 비슷해지도록 강약을 섞습니다.',
  SIMILAR: '실력이 비슷한 사람끼리 같은 조로 묶습니다.',
  RANDOM: '실력과 무관하게 무작위로 나눕니다.',
}

export const TEAM_ASSIGN_MODE_OPTIONS: Array<{ value: TeamAssignMode; label: string }> = [
  { value: TEAM_ASSIGN_MODE.BALANCED, label: TEAM_ASSIGN_MODE_LABEL.BALANCED },
  { value: TEAM_ASSIGN_MODE.SIMILAR, label: TEAM_ASSIGN_MODE_LABEL.SIMILAR },
  { value: TEAM_ASSIGN_MODE.RANDOM, label: TEAM_ASSIGN_MODE_LABEL.RANDOM },
]

/** 행사 리포트 공개 상태 (R-004) */
export const EVENT_RECAP_PUBLISH_STATUS = {
  DRAFT: 'DRAFT',
  /** 총무가 회장에게 승인 요청 — 2026-10-02 10/1 슬라이드 77 (DB tb_code 649 · Java ClubCodeConstants) */
  REVIEW_REQUESTED: 'REVIEW_REQUESTED',
  /** 회장이 사유와 함께 돌려보냄 (DB tb_code 650) */
  REJECTED: 'REJECTED',
  /** 회장이 승인함 — 공개 전. 공개 시점은 총무·부총무 «공개하기» (2026-10-15 사용자 결정 · DB tb_code 656) */
  APPROVED: 'APPROVED',
  PUBLISHED: 'PUBLISHED',
} as const
export type EventRecapPublishStatus = (typeof EVENT_RECAP_PUBLISH_STATUS)[keyof typeof EVENT_RECAP_PUBLISH_STATUS]

/** 라벨 = DB tb_code.ETC 와 같다(§7-B11 — 화면 «비공개» / DB «비공개(작성중)» 로 달랐다 → «작성 중») */
export const EVENT_RECAP_PUBLISH_STATUS_LABEL: Record<EventRecapPublishStatus, string> = {
  DRAFT: '작성 중',
  REVIEW_REQUESTED: '승인 대기',
  REJECTED: '반려',
  APPROVED: '승인 완료',
  PUBLISHED: '공개',
}

/**
 * 조편성 회장 승인 상태 — 2026-10-06 10/6 PPT 87번 장 (DB tb_code 651~655 · Java ClubCodeConstants.TEAM_REVIEW_STATUS_*)
 * 총무 «승인 요청» → 회장 «승인»(= 조편성 완료 + 회원 공지) / «반려»(사유 필수). 승인 대기 중 «바로 공지» 는 BYPASSED.
 */
export const TEAM_REVIEW_STATUS = {
  NONE: 'NONE',
  REQUESTED: 'REQUESTED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  BYPASSED: 'BYPASSED',
} as const
export type TeamReviewStatus = (typeof TEAM_REVIEW_STATUS)[keyof typeof TEAM_REVIEW_STATUS]

/** 라벨 = DB tb_code.ETC */
export const TEAM_REVIEW_STATUS_LABEL: Record<TeamReviewStatus, string> = {
  NONE: '요청 전',
  REQUESTED: '승인 대기',
  APPROVED: '승인',
  REJECTED: '반려',
  BYPASSED: '승인 없이 공지됨',
}

/** 감사인사 화자 구분 (R-004) */
export const THANKS_SPEAKER = {
  PRESIDENT: 'PRESIDENT',
  MANAGER: 'MANAGER',
} as const
export type ThanksSpeaker = (typeof THANKS_SPEAKER)[keyof typeof THANKS_SPEAKER]

export const THANKS_SPEAKER_LABEL: Record<ThanksSpeaker, string> = {
  PRESIDENT: '회장',
  MANAGER: '총무',
}

export const PARTICIPATION_STATUS = {
  ATTENDING: 'ATTENDING',
  ABSENT: 'ABSENT',
  UNDECIDED: 'UNDECIDED',
  WAITING: 'WAITING',
  CANCELLED: 'CANCELLED',
  ATTENDED: 'ATTENDED',
} as const
export type ParticipationStatus = (typeof PARTICIPATION_STATUS)[keyof typeof PARTICIPATION_STATUS]

/**
 * Vue 전용 참가 상태 (EventApiController 변환 후 값)
 *
 * DB 원본(PARTICIPATION_STATUS)과 다름:
 *   ATTENDING → ATTEND, UNDECIDED → PENDING
 *
 * @see docs/02_REFERENCE/CODE_CONSTANTS.md §5.2
 * @see front/.../EventApiController.java (양방향 변환)
 */
export const PARTICIPATION_VUE = {
  ATTEND: 'ATTEND',
  ABSENT: 'ABSENT',
  PENDING: 'PENDING',
  WAITING: 'WAITING',
} as const
export type ParticipationVue = (typeof PARTICIPATION_VUE)[keyof typeof PARTICIPATION_VUE]

/**
 * 4차 발주(통합본 p12): '참석' → '참가' 명칭변경, 노출순서 참가/불참/대기/미정.
 * 코드값(ATTEND)은 불변 — 라벨만 변경한다.
 *
 * 발주 32(2026-09-29): «대기» → **«대기자신청»**. 승인 전 상태(APPROVAL_STATUS=PENDING)는 «승인대기» 로 따로 부른다
 * (APPROVAL_STATUS_LABEL). 대기자신청은 라운딩만 받는다 — useEventParticipation.supportsWaitlist.
 */
export const PARTICIPATION_VUE_LABEL: Record<ParticipationVue, string> = {
  ATTEND: '참가',
  ABSENT: '불참',
  WAITING: '대기자신청',
  PENDING: '미정',
}

export const APPROVAL_STATUS = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
} as const
export type ApprovalStatus = (typeof APPROVAL_STATUS)[keyof typeof APPROVAL_STATUS]

/**
 * 행사 참가 «승인 상태» 표기 — 발주 32(2026-09-29).
 * 종전엔 승인 전(PENDING)을 «대기» 라 불러 참가 상태의 대기자(WAITING)와 구분되지 않았다.
 * CMS(club/event/manage/view.jsp)의 배지와 같은 말을 쓴다.
 */
export const APPROVAL_STATUS_LABEL: Record<ApprovalStatus, string> = {
  PENDING: '승인대기',
  APPROVED: '승인',
  REJECTED: '반려',
}

/**
 * 참가 «특별요청사항» 상태(tb_club_event_participant.SPECIAL_REQUEST_STATUS) — 값은 APPROVAL_STATUS 를 그대로 쓴다.
 * 2026-10-03 10/2본 93~98번 장. 참가 승인과 별개라 «반려» 대신 «거절»(화면 버튼 «요청 승인 / 요청 거절»과 같은 말).
 * Java = ClubCodeConstants.APPROVAL_STATUS_*.
 */
export const SPECIAL_REQUEST_STATUS_LABEL: Record<ApprovalStatus, string> = {
  PENDING: '승인 대기',
  APPROVED: '승인',
  REJECTED: '거절',
}

export const EVENT_PAYMENT_STATUS = {
  UNPAID: 'UNPAID',
  PAID: 'PAID',
} as const
export type EventPaymentStatus = (typeof EVENT_PAYMENT_STATUS)[keyof typeof EVENT_PAYMENT_STATUS]

export const TOURNAMENT_STATUS = {
  DRAFT: 'DRAFT',
  RECRUITING: 'RECRUITING',
  OPEN: 'OPEN',
  CLOSED: 'CLOSED',
  ONGOING: 'ONGOING',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
} as const
export type TournamentStatus = (typeof TOURNAMENT_STATUS)[keyof typeof TOURNAMENT_STATUS]

export const URGENT_JOIN_STATUS = {
  RECRUITING: 'RECRUITING',
  CLOSED: 'CLOSED',
} as const
export type UrgentJoinStatus = (typeof URGENT_JOIN_STATUS)[keyof typeof URGENT_JOIN_STATUS]

// ========================================
// 양수/양도/물물거래 — 거래유형 / 유상무상 (4차발주 3편 p10 · R-3-10-2)
// DB 공통코드 URGENT_JOIN_TRADE_TYPE · URGENT_JOIN_PRICE_TYPE 와 3계층 정합
// ========================================
export const URGENT_JOIN_TRADE_TYPE = {
  ACQUIRE: 'ACQUIRE',
  TRANSFER: 'TRANSFER',
  JOIN: 'JOIN',
  EXCHANGE: 'EXCHANGE',
} as const
export type UrgentJoinTradeType = (typeof URGENT_JOIN_TRADE_TYPE)[keyof typeof URGENT_JOIN_TRADE_TYPE]

/** 목록 제목 앞 머릿글에 쓰는 라벨 — 예: [양수] */
export const URGENT_JOIN_TRADE_TYPE_LABEL: Record<string, string> = {
  ACQUIRE: '양수',
  TRANSFER: '양도',
  JOIN: '조인',
  EXCHANGE: '교환',
}

export const URGENT_JOIN_PRICE_TYPE = {
  PAID: 'PAID',
  FREE: 'FREE',
} as const
export type UrgentJoinPriceType = (typeof URGENT_JOIN_PRICE_TYPE)[keyof typeof URGENT_JOIN_PRICE_TYPE]

export const URGENT_JOIN_PRICE_TYPE_LABEL: Record<string, string> = {
  PAID: '유상',
  FREE: '무상',
}

// ========================================
// 카풀 유형
// ========================================

export const CARPOOL_TYPE = {
  TOGETHER: 'TOGETHER',
  RIDE: 'RIDE',
} as const
export type CarpoolType = (typeof CARPOOL_TYPE)[keyof typeof CARPOOL_TYPE]

export const CARPOOL_TYPE_LABEL: Record<CarpoolType, string> = {
  TOGETHER: '같이가요',
  RIDE: '태워주세요',
}

// ========================================
// 동호회 가입 정책
// ========================================

export const JOIN_POLICY = {
  AUTO: 'AUTO',
  OPEN: 'OPEN',
  PASSWORD: 'PASSWORD',
  SUBJECTIVE: 'SUBJECTIVE',
} as const
export type JoinPolicy = (typeof JOIN_POLICY)[keyof typeof JOIN_POLICY]

export const JOIN_POLICY_LABEL: Record<JoinPolicy, string> = {
  AUTO: '자동승인',
  OPEN: '공개',
  PASSWORD: '비공개',
  SUBJECTIVE: '주관식질문',
}

// ========================================
// 가입 질문 유형
// ========================================

export const QUESTION_TYPE = {
  TEXT: 'TEXT',
  CHOICE: 'CHOICE',
  MULTIPLE: 'MULTIPLE',
} as const
export type QuestionType = (typeof QUESTION_TYPE)[keyof typeof QUESTION_TYPE]

// ========================================
// 게시판
// ========================================

export const BOARD_TYPE = {
  NOTICE: 'NOTICE',
  EVENT_NOTICE: 'EVENT_NOTICE',
  PHOTO: 'PHOTO',
  FREE: 'FREE',
  CARPOOL: 'CARPOOL',
  SPONSOR: 'SPONSOR',
  VOTE: 'VOTE',
  GREETING: 'GREETING',
  RESTAURANT: 'RESTAURANT',
  CELEBRATION: 'CELEBRATION',
  /** 4차발주 F-9(통합본 p113) — 재무리포트 > 기간리포트 게시판 */
  FINANCE_REPORT: 'FINANCE_REPORT',
} as const
export type BoardType = (typeof BOARD_TYPE)[keyof typeof BOARD_TYPE]

export const BOARD_TYPE_LABEL: Record<BoardType, string> = {
  NOTICE: '공지사항',
  EVENT_NOTICE: '행사공지',
  PHOTO: '포토갤러리',
  FREE: '자유게시판',
  CARPOOL: '카풀',
  SPONSOR: '스폰서',
  VOTE: '투표',
  GREETING: '가입인사',
  RESTAURANT: '맛집',
  CELEBRATION: '경조사',
  FINANCE_REPORT: '재무리포트',
}

export const BOARD_CATEGORY = {
  JOIN: 'JOIN',
  FOOD: 'FOOD',
  CONDOLENCE: 'CONDOLENCE',
  GENERAL: 'GENERAL',
} as const
export type BoardCategory = (typeof BOARD_CATEGORY)[keyof typeof BOARD_CATEGORY]

// ========================================
// 투표
// ========================================

export const VOTE_TYPE = {
  SINGLE: 'SINGLE',
  MULTIPLE: 'MULTIPLE',
} as const
export type VoteType = (typeof VOTE_TYPE)[keyof typeof VOTE_TYPE]

export const VOTE_STATUS = {
  ONGOING: 'ONGOING',
  ENDED: 'ENDED',
} as const
export type VoteStatus = (typeof VOTE_STATUS)[keyof typeof VOTE_STATUS]

// ========================================
// 회비/정산
// ========================================

export const FEE_TYPE = {
  MONTHLY: 'MONTHLY',
  ENTRY: 'ENTRY',
  SPECIAL: 'SPECIAL',
} as const
export type FeeType = (typeof FEE_TYPE)[keyof typeof FEE_TYPE]

export const FEE_PAYMENT_STATUS = {
  UNPAID: 'UNPAID',
  PAID: 'PAID',
  OVERDUE: 'OVERDUE',
  EXCEPTION: 'EXCEPTION',
  DONATION: 'DONATION', // 기부 (2026-07-24, 3차 slide 94)
} as const
export type FeePaymentStatus = (typeof FEE_PAYMENT_STATUS)[keyof typeof FEE_PAYMENT_STATUS]
export const FEE_PAYMENT_STATUS_LABEL: Record<string, string> = {
  UNPAID: '미납',
  PAID: '납부',
  OVERDUE: '연체',
  EXCEPTION: '예외',
  DONATION: '기부',
}

export const SETTLEMENT_STATUS = {
  OPEN: 'OPEN',
  PARTIAL: 'PARTIAL',
  COMPLETED: 'COMPLETED',
  CLOSED: 'CLOSED',
} as const
export type SettlementStatus = (typeof SETTLEMENT_STATUS)[keyof typeof SETTLEMENT_STATUS]

/** 정산 유형 — 2종만 존재 (행사정산 / 일반정산). 백엔드 EVENT_SEQ 유무 파생값과 일치 */
export const SETTLEMENT_TYPE = {
  EVENT: 'EVENT',
  GENERAL: 'GENERAL',
} as const
export type SettlementType = (typeof SETTLEMENT_TYPE)[keyof typeof SETTLEMENT_TYPE]

export const SETTLEMENT_PAYMENT_STATUS = {
  UNPAID: 'UNPAID',
  PARTIAL: 'PARTIAL',
  PAID: 'PAID',
} as const
export type SettlementPaymentStatus = (typeof SETTLEMENT_PAYMENT_STATUS)[keyof typeof SETTLEMENT_PAYMENT_STATUS]

export const SETTLEMENT_ITEM = {
  CART: 'CART',
  SHADE: 'SHADE',
  MEAL_BEFORE: 'MEAL_BEFORE',
  MEAL_AFTER: 'MEAL_AFTER',
  CADDIE: 'CADDIE',
  DRINK: 'DRINK',
  PRIZE: 'PRIZE',
  OTHER: 'OTHER',
} as const
export type SettlementItem = (typeof SETTLEMENT_ITEM)[keyof typeof SETTLEMENT_ITEM]

// ========================================
// 쇼핑몰/주문
// ========================================

export const ORDER_STATUS = {
  PENDING: 'PENDING',
  PAID: 'PAID',
  PREPARING: 'PREPARING',
  SHIPPING: 'SHIPPING',
  DELIVERED: 'DELIVERED',
  CANCELLED: 'CANCELLED',
  REFUNDED: 'REFUNDED',
  COMPLETED: 'COMPLETED',
  // 환불/교환/취소 신청 대기 — 관리자 승인 전 임시 상태
  PENDING_CANCEL: 'PENDING_CANCEL',
  PENDING_RETURN: 'PENDING_RETURN',
  PENDING_EXCHANGE: 'PENDING_EXCHANGE',
  EXCHANGED: 'EXCHANGED',
} as const
export type OrderStatus = (typeof ORDER_STATUS)[keyof typeof ORDER_STATUS]

export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  PENDING: '결제대기',
  PAID: '결제완료',
  PREPARING: '상품준비중',
  SHIPPING: '배송중',
  DELIVERED: '배송완료',
  CANCELLED: '주문취소',
  REFUNDED: '환불완료',
  COMPLETED: '배송완료',
  PENDING_CANCEL: '취소 신청 중',
  PENDING_RETURN: '반품 신청 중',
  PENDING_EXCHANGE: '교환 신청 중',
  EXCHANGED: '교환 완료',
}

export const DELIVERY_STATUS = {
  PREPARING: 'PREPARING',
  SHIPPING: 'SHIPPING',
  DELIVERED: 'DELIVERED',
  CANCELLED: 'CANCELLED',
} as const
export type DeliveryStatus = (typeof DELIVERY_STATUS)[keyof typeof DELIVERY_STATUS]

export const DELIVERY_COMPANY = {
  CJ: 'CJ',
  HANJIN: 'HANJIN',
  LOTTE: 'LOTTE',
  LOGEN: 'LOGEN',
  KDEXP: 'KDEXP',
  POST: 'POST',
  DIRECT: 'DIRECT',
} as const
export type DeliveryCompany = (typeof DELIVERY_COMPANY)[keyof typeof DELIVERY_COMPANY]

// ========================================
// 공동구매
// ========================================

export const GROUP_PURCHASE_STATUS = {
  DRAFT: 'DRAFT',
  RECRUITING: 'RECRUITING',
  CONFIRMED: 'CONFIRMED',
  CANCELLED: 'CANCELLED',
  COMPLETED: 'COMPLETED',
} as const
export type GroupPurchaseStatus = (typeof GROUP_PURCHASE_STATUS)[keyof typeof GROUP_PURCHASE_STATUS]

export const GROUP_PURCHASE_STATUS_LABEL: Record<string, string> = {
  DRAFT: '진행예정',
  RECRUITING: '진행중',
  CONFIRMED: '구매확정',
  CANCELLED: '취소',
  COMPLETED: '완료',
}

export const GROUP_PURCHASE_ORDER_STATUS = {
  APPLIED: 'APPLIED',
  CONFIRMED: 'CONFIRMED',
  CANCELLED: 'CANCELLED',
  REFUNDED: 'REFUNDED',
} as const
export type GroupPurchaseOrderStatus = (typeof GROUP_PURCHASE_ORDER_STATUS)[keyof typeof GROUP_PURCHASE_ORDER_STATUS]

// ========================================
// 환불
// ========================================

export const REFUND_TYPE = {
  CANCEL: 'CANCEL',
  RETURN: 'RETURN',
  EXCHANGE: 'EXCHANGE',
} as const
export type RefundType = (typeof REFUND_TYPE)[keyof typeof REFUND_TYPE]

export const REFUND_STATUS = {
  REQUESTED: 'REQUESTED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  COMPLETED: 'COMPLETED',
} as const
export type RefundStatus = (typeof REFUND_STATUS)[keyof typeof REFUND_STATUS]

/**
 * 회원탈퇴 사유(DROP_CODE) — 2026-10-01 발주 9/30 슬라이드 12 결정 ①(저장 + 필수 + 기본 선택 없음 · 발주 요청 문구).
 * DB tb_code(CODE='DROP_CODE', CODE_NM) · Java ClubCodeConstants.DROP_REASON_* 와 같다(C-07). 기타는 내용 필수.
 */
export const DROP_REASON = {
  CLUB_STOPPED: 'CLUB_STOPPED',
  NOT_NEEDED: 'NOT_NEEDED',
  OTHER: 'OTHER',
} as const
export type DropReason = (typeof DROP_REASON)[keyof typeof DROP_REASON]
export const DROP_REASON_LABEL: Record<DropReason, string> = {
  CLUB_STOPPED: '동호회 활동을 중지함',
  NOT_NEEDED: '더 이상 서비스가 불필요해짐',
  OTHER: '기타',
}
export const DROP_REASON_OPTIONS = (Object.keys(DROP_REASON) as DropReason[]).map(code => ({
  code,
  label: DROP_REASON_LABEL[code],
}))
/** 기타 사유 최대 글자 수 — tb_user.DROP_REASON_DETAIL(mediumtext) 이지만 화면·서버가 같은 상한을 쓴다 */
export const DROP_REASON_DETAIL_MAX = 500

export const REFUND_REASON = {
  CHANGE_MIND: 'CHANGE_MIND',
  DEFECT: 'DEFECT',
  WRONG_PRODUCT: 'WRONG_PRODUCT',
  DESCRIPTION_DIFF: 'DESCRIPTION_DIFF',
  DELIVERY_DELAY: 'DELIVERY_DELAY',
  ETC: 'ETC',
} as const
export type RefundReason = (typeof REFUND_REASON)[keyof typeof REFUND_REASON]

// 한글 라벨 매핑 (PC OrderActionModal / 모바일 OrderActionSheet 공용)
export const REFUND_REASON_LABEL: Record<RefundReason, string> = {
  CHANGE_MIND: '단순 변심',
  DEFECT: '상품 불량',
  WRONG_PRODUCT: '오배송',
  DESCRIPTION_DIFF: '상품 설명과 다름',
  DELIVERY_DELAY: '배송 지연',
  ETC: '기타',
}

export const REFUND_TYPE_LABEL: Record<RefundType, string> = {
  CANCEL: '주문 취소',
  RETURN: '반품',
  EXCHANGE: '교환',
}

export const REFUND_STATUS_LABEL: Record<RefundStatus, string> = {
  REQUESTED: '신청 접수',
  APPROVED: '승인',
  REJECTED: '거부',
  COMPLETED: '완료',
}

// 사유 옵션 배열 (select/dropdown 용)
export const REFUND_REASON_OPTIONS = (Object.keys(REFUND_REASON) as RefundReason[]).map(code => ({
  code,
  label: REFUND_REASON_LABEL[code],
}))

// D-3 (Sprint 3): 사유별 가이드 안내문 + 부담 주체
//  - feeBearer: 'CUSTOMER' (고객 부담) / 'SELLER' (골총비 부담)
//  - tips: 사용자에게 표시할 안내 사항
export interface RefundReasonGuide {
  feeBearer: 'CUSTOMER' | 'SELLER'
  feeText: string
  tips: string[]
}

export const REFUND_REASON_GUIDE: Record<RefundReason, RefundReasonGuide> = {
  CHANGE_MIND: {
    feeBearer: 'CUSTOMER',
    feeText: '왕복 배송비를 환불 금액에서 차감합니다.',
    tips: [
      '상품이 미개봉/미사용 상태여야 합니다.',
      '왕복 배송비 5,000원이 차감됩니다.',
      '주문 후 7일 이내에만 신청 가능합니다.',
    ],
  },
  DEFECT: {
    feeBearer: 'SELLER',
    feeText: '회수비/배송비는 골총비가 부담합니다.',
    tips: [
      '불량 부위 사진 첨부를 권장합니다 (검수 시 활용).',
      '배송비는 모두 골총비가 부담합니다.',
      '검수 후 동일 상품 재배송 또는 환불 가능합니다.',
    ],
  },
  WRONG_PRODUCT: {
    feeBearer: 'SELLER',
    feeText: '회수비/배송비는 골총비가 부담합니다.',
    tips: [
      '잘못 배송된 상품 사진 첨부를 권장합니다.',
      '주문 상품과 다른 상품을 받으신 경우입니다.',
      '회수 즉시 정상 상품 재발송됩니다.',
    ],
  },
  DESCRIPTION_DIFF: {
    feeBearer: 'SELLER',
    feeText: '회수비/배송비는 골총비가 부담합니다.',
    tips: [
      '상품 설명과 다른 부분의 사진을 첨부해주세요.',
      '관리자 검토 후 환불이 진행됩니다.',
    ],
  },
  DELIVERY_DELAY: {
    feeBearer: 'SELLER',
    feeText: '배송 지연 보상금은 별도 정책에 따릅니다.',
    tips: [
      '배송 출발 후 7일 이상 지연 시 신청 가능합니다.',
      '배송 추적 정보가 자동으로 첨부됩니다.',
    ],
  },
  ETC: {
    feeBearer: 'CUSTOMER',
    feeText: '관리자 검토 후 부담 주체가 결정됩니다.',
    tips: [
      '상세한 사유를 [상세 설명] 에 입력해주세요.',
      '관리자 검토에 시간이 소요될 수 있습니다.',
    ],
  },
}

// ========================================
// 고객센터
// ========================================

export const FAQ_CATEGORY = {
  MEMBER: 'MEMBER',
  CLUB: 'CLUB',
  ORDER_PAYMENT: 'ORDER_PAYMENT',
  DELIVERY: 'DELIVERY',
  CANCEL_REFUND: 'CANCEL_REFUND',
  RETURN: 'RETURN',
  MALL: 'MALL',
  ETC: 'ETC',
} as const
export type FaqCategory = (typeof FAQ_CATEGORY)[keyof typeof FAQ_CATEGORY]

export const QNA_STATUS = {
  RECEIPT: 'RECEIPT',
  ONGOING: 'ONGOING',
  COMPLETED: 'COMPLETED',
} as const
export type QnaStatus = (typeof QNA_STATUS)[keyof typeof QNA_STATUS]

/** Vue 전용 문의 표시 상태 (answerContents 유무로 프론트에서 분류) */
export const INQUIRY_DISPLAY_STATUS = {
  PENDING: 'PENDING',
  ANSWERED: 'ANSWERED',
} as const
export type InquiryDisplayStatus = (typeof INQUIRY_DISPLAY_STATUS)[keyof typeof INQUIRY_DISPLAY_STATUS]

// ========================================
// 신고
// ========================================

export const REPORT_TYPE = {
  SPAM: 'SPAM',
  ABUSE: 'ABUSE',
  ADULT: 'ADULT',
  VIOLENCE: 'VIOLENCE',
  PERSONAL_INFO: 'PERSONAL_INFO',
  COPYRIGHT: 'COPYRIGHT',
  OTHER: 'OTHER',
} as const
export type ReportType = (typeof REPORT_TYPE)[keyof typeof REPORT_TYPE]

export const REPORT_STATUS = {
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  COMPLETED: 'COMPLETED',
  REJECTED: 'REJECTED',
} as const
export type ReportStatus = (typeof REPORT_STATUS)[keyof typeof REPORT_STATUS]

// ========================================
// 뉴스/스폰서
// ========================================

export const NEWS_CATEGORY = {
  ALL: 'ALL',
  TOUR: 'TOUR',
  EQUIPMENT: 'EQUIPMENT',
  COURSE: 'COURSE',
  TIP: 'TIP',
} as const
export type NewsCategory = (typeof NEWS_CATEGORY)[keyof typeof NEWS_CATEGORY]

export const SPONSOR_TYPE = {
  MONEY: 'MONEY',
  GOODS: 'GOODS',
  SERVICE: 'SERVICE',
} as const
export type SponsorType = (typeof SPONSOR_TYPE)[keyof typeof SPONSOR_TYPE]

// ========================================
// 약관/구독
// ========================================

export const TERMS_TYPE = {
  SERVICE: 'SERVICE',
  PAYMENT_POLICY: 'PAYMENT_POLICY',
  PRIVACY: 'PRIVACY',
  LOCATION: 'LOCATION',
  THIRD_PARTY: 'THIRD_PARTY',
  MARKETING: 'MARKETING',
  REFUND_EXCHANGE: 'REFUND_EXCHANGE',
  ECOMMERCE: 'ECOMMERCE',
  POINT: 'POINT',
  REWARD: 'REWARD',
  AUTO_PAYMENT: 'AUTO_PAYMENT',
  CLUB_CREATE: 'CLUB_CREATE',
} as const
export type TermsType = (typeof TERMS_TYPE)[keyof typeof TERMS_TYPE]

export const SUBSCRIPTION_STATUS = {
  TRIAL: 'TRIAL',
  ACTIVE: 'ACTIVE',
  GRACE_PERIOD: 'GRACE_PERIOD',
  SUSPENDED: 'SUSPENDED',
  CANCELLED: 'CANCELLED',
  /** 관리자 무료 지정(2026-09-10) — 기간 무관 무료. 만료 판정을 타지 않는다. 서버 ClubCodeConstants 와 정합 */
  COMPLIMENTARY: 'COMPLIMENTARY',
} as const
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUS)[keyof typeof SUBSCRIPTION_STATUS]

// 요금제 4종 재정의(2026-07-08): 월/12/24/36개월. 서버 ClubCodeConstants 와 정합.
//   HALF_YEARLY(6개월)는 폐지 대상이나, 기존 화면(ClubSubscriptionPage/ClubSettingPage)의 하드코딩 카드가
//   아직 참조하므로 레거시 키로 남겨 컴파일 정합 유지 — 카드 UI 4종화 완료 후 제거. ACTIVE 목록에선 제외.
export const PLAN_TYPE = {
  MONTHLY: 'MONTHLY',     // 월간 (30일)
  YEARLY: 'YEARLY',       // 12개월 (365일)
  BIENNIAL: 'BIENNIAL',   // 24개월 (730일)
  TRIENNIAL: 'TRIENNIAL', // 36개월 (1095일)
  /** @deprecated 6개월제 폐지 — 레거시 화면 호환용. 신규 노출 금지 */
  HALF_YEARLY: 'HALF_YEARLY',
} as const
export type PlanType = (typeof PLAN_TYPE)[keyof typeof PLAN_TYPE]

/** 노출·판매 대상 요금제(순서 고정: 월→12→24→36) */
export const ACTIVE_PLAN_TYPES = ['MONTHLY', 'YEARLY', 'BIENNIAL', 'TRIENNIAL'] as const

/** planType 표시 라벨 (월간/12개월/24개월/36개월) */
export const PLAN_TYPE_LABEL: Record<string, string> = {
  MONTHLY: '월간',
  YEARLY: '12개월',
  BIENNIAL: '24개월',
  TRIENNIAL: '36개월',
  HALF_YEARLY: '6개월',
}

// ========================================
// 공통/검색
// ========================================

export const SEARCH_TYPE = {
  TITLE: 'TITLE',
  CONTENT: 'CONTENT',
  TITLE_CONTENT: 'TITLE_CONTENT',
  WRITER: 'WRITER',
  ALL: 'ALL',
} as const
export type SearchType = (typeof SEARCH_TYPE)[keyof typeof SEARCH_TYPE]

export const SORT_ORDER = {
  LATEST: 'LATEST',
  OLDEST: 'OLDEST',
  VIEWS: 'VIEWS',
  LIKES: 'LIKES',
  COMMENTS: 'COMMENTS',
  NAME_ASC: 'NAME_ASC',
  NAME_DESC: 'NAME_DESC',
  SCORE_ASC: 'SCORE_ASC',
  SCORE_DESC: 'SCORE_DESC',
} as const
export type SortOrder = (typeof SORT_ORDER)[keyof typeof SORT_ORDER]

// ========================================
// 시상 유형 (AWARD_TYPE)
// ========================================

export const AWARD_TYPE = {
  MEDALIST: 'MEDALIST',
  WIN: 'WIN',
  RUNNER_UP: 'RUNNER_UP',
  THIRD_PLACE: 'THIRD_PLACE',
  FOURTH_PLACE: 'FOURTH_PLACE',
  LONGEST: 'LONGEST',
  NEAREST: 'NEAREST',
  BIRDIE: 'BIRDIE',
  BEST_SCORE: 'BEST_SCORE',
  BEST_NET: 'BEST_NET',
  BOGEY_FREE: 'BOGEY_FREE',
  ALL_BOGEY: 'ALL_BOGEY',
  ALL_PAR: 'ALL_PAR',
  ALL_BIRDIE: 'ALL_BIRDIE',
  EAGLE: 'EAGLE',
  HOLE_IN_ONE: 'HOLE_IN_ONE',
  ALBATROSS: 'ALBATROSS',
  BEST_DRESSER: 'BEST_DRESSER',
  BEST_PHOTO: 'BEST_PHOTO',
  MANNER: 'MANNER',
  BOOBY_PRIZE: 'BOOBY_PRIZE',
  ALMOST_LAST: 'ALMOST_LAST',
  BREAK_100_MALE: 'BREAK_100_MALE',
  BREAK_100_FEMALE: 'BREAK_100_FEMALE',
  SINGLE_PLAYER: 'SINGLE_PLAYER',
  LUCKY: 'LUCKY',
  TEAM_SIDE: 'TEAM_SIDE',
  TEAM_PHOTO: 'TEAM_PHOTO',
} as const
export type AwardType = (typeof AWARD_TYPE)[keyof typeof AWARD_TYPE]

export const AWARD_TYPE_LABEL: Record<AwardType, string> = {
  MEDALIST: '메달리스트',
  WIN: '우승',
  RUNNER_UP: '준우승',
  THIRD_PLACE: '3위',
  FOURTH_PLACE: '4위',
  LONGEST: '롱기스트',
  NEAREST: '니어핀',
  BIRDIE: '버디상',
  BEST_SCORE: '베스트스코어',
  BEST_NET: '베스트넷스코어',
  BOGEY_FREE: '노보기상',
  ALL_BOGEY: '다보기',
  ALL_PAR: '다파',
  ALL_BIRDIE: '다버디',
  EAGLE: '이글상',
  HOLE_IN_ONE: '홀인원상',
  ALBATROSS: '알바트로스',
  BEST_DRESSER: '베스트드레서상',
  BEST_PHOTO: '베스트포토상',
  MANNER: '매너상',
  BOOBY_PRIZE: '부비상(꼴찌)',
  ALMOST_LAST: '아차상',
  BREAK_100_MALE: '백돌이 탈출',
  BREAK_100_FEMALE: '백순이 탈출',
  SINGLE_PLAYER: '싱글플레이어상',
  LUCKY: '행운상',
  TEAM_SIDE: '청백전 승리',
  TEAM_PHOTO: '단체 포토제닉',
}

// ========================================
// 기네스 유형 (GUINNESS_TYPE)
// ========================================

/**
 * ⚠️ C-07 3계층 정합 — 2026-08-19 실DB 기준으로 정정.
 *
 * 기존 상수 13종은 **두 계열을 뒤섞고 라벨까지 잘못 붙어** 있었다.
 * 실제로는 값이 나오는 곳이 두 군데이고, 서로 다른 개념이다.
 *
 * **2026-08-19 발주 확정 — 기네스는 '다버디(최다)' 기준.** 이전에는 어휘가 두 계열로 갈려 있었다.
 *
 * | 계열 | 뜻 | 현재 |
 * |---|---|---|
 * | `MOST_*` · `BEST_PUTTING` | **최다/최소** — 다버디 = 버디를 가장 많이 | ✅ **정식** (설정 `tb_club_guinness_config` 3DB 공통 14종) |
 * | `ALL_*` · `EAGLE` | **전 홀 동일** — 올버디 = 18홀 모두 버디 | ⛔ **폐기** |
 *
 * 폐기 근거 — 실측(dev, 참가자 47명):
 *   · 올버디 해당자 **0명**(최다 버디도 10홀). 18홀 전 홀 버디는 PGA 에도 없어 영원히 미발동
 *   · 올파 '해당자' 3명은 **전 홀 PAR=SCORE 인 미입력·기본값 데이터** — 성적을 안 넣은 참가자가
 *     자동으로 기네스 수상자가 되는 부작용이 있었다
 *   · 다버디는 상대평가라 **행사마다 수상자가 나온다**
 *
 * 기존 상수는 `ALL_BIRDIE` 를 '다버디'로 표기해 두 개념을 같은 것으로 오인하게 했다.
 *
 * 라벨은 실DB `GUINNESS_NAME` 을 정본으로 쓴다(화면이 이미 그 값으로 렌더되고 있어 어긋나면 안 된다).
 */
export const GUINNESS_TYPE = {
  // ── 설정·집계 공통 7종 ──────────────────────────────
  SHINPERIO_LOWEST: 'SHINPERIO_LOWEST',
  LOWEST_SCORE: 'LOWEST_SCORE',
  HIGHEST_SCORE: 'HIGHEST_SCORE',
  HOLE_IN_ONE: 'HOLE_IN_ONE',
  ALBATROSS: 'ALBATROSS',
  OLDEST_PLAYER: 'OLDEST_PLAYER',
  YOUNGEST_PLAYER: 'YOUNGEST_PLAYER',
  // ── 설정 전용 (수동 입력) ───────────────────────────
  LONGEST_DRIVE: 'LONGEST_DRIVE',
  NEAREST: 'NEAREST',
  // ── 최다/최소 (2026-08-19 확정 — 자동집계 전환 완료) ──
  MOST_BIRDIE: 'MOST_BIRDIE',
  MOST_BOGEY: 'MOST_BOGEY',
  MOST_PAR: 'MOST_PAR',
  MOST_EAGLE: 'MOST_EAGLE',
  BEST_PUTTING: 'BEST_PUTTING',
} as const
export type GuinnessType = (typeof GUINNESS_TYPE)[keyof typeof GUINNESS_TYPE]

export const GUINNESS_TYPE_LABEL: Record<GuinnessType, string> = {
  SHINPERIO_LOWEST: '신페리오 최저타',
  LOWEST_SCORE: '최저타',
  HIGHEST_SCORE: '최다타',
  HOLE_IN_ONE: '홀인원횟수',
  ALBATROSS: '알바트로스 횟수',
  OLDEST_PLAYER: '최고령 참여자',
  YOUNGEST_PLAYER: '최연소 참여자',
  LONGEST_DRIVE: '롱기스트',
  NEAREST: '니어핀', // D29-b(2026-10-13): 시상 코드 라벨 «니어핀» 으로 통일
  MOST_BIRDIE: '다버디',
  MOST_BOGEY: '다보기',
  MOST_PAR: '다파',
  MOST_EAGLE: '이글 횟수',
  BEST_PUTTING: '최소 퍼팅',
}

/** 설정 화면(`tb_club_guinness_config`)에 실제로 존재하는 14종 — 3DB 공통 */
export const GUINNESS_TYPE_CONFIGURABLE: GuinnessType[] = [
  GUINNESS_TYPE.SHINPERIO_LOWEST, GUINNESS_TYPE.LOWEST_SCORE, GUINNESS_TYPE.HIGHEST_SCORE,
  GUINNESS_TYPE.HOLE_IN_ONE, GUINNESS_TYPE.ALBATROSS, GUINNESS_TYPE.OLDEST_PLAYER,
  GUINNESS_TYPE.YOUNGEST_PLAYER, GUINNESS_TYPE.LONGEST_DRIVE, GUINNESS_TYPE.NEAREST,
  GUINNESS_TYPE.MOST_BIRDIE, GUINNESS_TYPE.MOST_BOGEY, GUINNESS_TYPE.MOST_PAR,
  GUINNESS_TYPE.MOST_EAGLE, GUINNESS_TYPE.BEST_PUTTING,
]

/**
 * 행사 성적에서 자동집계되는 12종 — 조회 시점에 `ScoreApiController.computeAutoGuinness` 가 계산한다.
 * (2026-08-31: 행사 단위로 기네스 행을 INSERT 하던 `calculateGuinnessForEvent` 는 호출부가 없어 제거.
 *  기록은 저장하지 않고 조회할 때마다 최고기록을 다시 뽑는다 — CMS 승인된 수동 등록이 우선.)
 * 나머지 2종(`LONGEST_DRIVE`·`NEAREST`)은 수동 입력이라 집계가 없는 것이 정상이다.
 */
export const GUINNESS_TYPE_AUTO_CALC: GuinnessType[] = [
  GUINNESS_TYPE.SHINPERIO_LOWEST, GUINNESS_TYPE.LOWEST_SCORE, GUINNESS_TYPE.HIGHEST_SCORE,
  GUINNESS_TYPE.MOST_BIRDIE, GUINNESS_TYPE.MOST_BOGEY, GUINNESS_TYPE.MOST_PAR,
  GUINNESS_TYPE.MOST_EAGLE, GUINNESS_TYPE.BEST_PUTTING,
  GUINNESS_TYPE.HOLE_IN_ONE, GUINNESS_TYPE.ALBATROSS,
  GUINNESS_TYPE.OLDEST_PLAYER, GUINNESS_TYPE.YOUNGEST_PLAYER,
]

// ========================================
// 명예의전당 카테고리 (HALLOFFAME_CATEGORY)
// ========================================

export const HALLOFFAME_CATEGORY = {
  MEDALIST: 'MEDALIST',
  CHAMPION: 'CHAMPION',
  NEAREST: 'NEAREST',
  LONGEST: 'LONGEST',
} as const
export type HallOfFameCategory = (typeof HALLOFFAME_CATEGORY)[keyof typeof HALLOFFAME_CATEGORY]

export const HALLOFFAME_CATEGORY_LABEL: Record<HallOfFameCategory, string> = {
  MEDALIST: '메달리스트',
  CHAMPION: '우승',
  NEAREST: '니어핀', // D29-b(2026-10-13): 시상 코드 라벨 «니어핀» 으로 통일
  LONGEST: '롱기스트',
}

// ========================================
// 골총캐쉬 자금원천 (FUNDING_SOURCE)
// DB tb_code / Java ClubCodeConstants 와 3계층 일치. 값 불변(NICEPAY|APPLE_IAP|GOOGLE_IAP|ADMIN)
// ========================================
export const FUNDING_SOURCE = {
  NICEPAY: 'NICEPAY',
  APPLE_IAP: 'APPLE_IAP',
  GOOGLE_IAP: 'GOOGLE_IAP',
  ADMIN: 'ADMIN',
} as const
export type FundingSource = (typeof FUNDING_SOURCE)[keyof typeof FUNDING_SOURCE]

export const FUNDING_SOURCE_LABEL: Record<FundingSource, string> = {
  NICEPAY: '웹 PG(나이스페이)',
  APPLE_IAP: '앱스토어 인앱결제',
  GOOGLE_IAP: '구글플레이 인앱결제',
  ADMIN: '관리자 수동충전',
}

/** 원천별 사용·환불 근거 (값 신규 생성 아님 — 확정 정책 표시문구) */
export const FUNDING_SOURCE_USAGE: Record<FundingSource, string> = {
  NICEPAY: '쇼핑몰·환불 가능',
  // ⛔ 2026-09-25 교정 — 종전 «구독만» 은 사실과 달랐다(프리미엄·회비·이체에도 쓰인다). 막힌 것은 쇼핑몰뿐
  APPLE_IAP: '쇼핑몰 제외 · 스토어 환불',
  GOOGLE_IAP: '쇼핑몰 제외 · 스토어 환불',
  ADMIN: '쇼핑몰 제외 · 환불 불가',
}

/** 잔액 카드 범례 — 기존 12px/#888 타이포만 사용 */
export const FUNDING_SOURCE_LEGEND = [
  '웹 PG 충전: 쇼핑몰·구독·환불 가능',
  '인앱결제: 쇼핑몰 제외 사용 가능, 환불은 스토어에서',
  '관리자 지급: 쇼핑몰 제외 사용 가능, 환불 불가',
] as const

/** 적립금(ACCUMULATED) 행 — POINT_TYPE 기존 구분 유지 */
export const ACCUMULATED_USAGE_LABEL = '적립금 · 쇼핑몰만'

/** 과거 미태깅 lot 은 NICEPAY 로 본다(소급 UPDATE 없음). */
export function resolveFundingSource(src?: string | null): FundingSource {
  if (src === FUNDING_SOURCE.APPLE_IAP) return FUNDING_SOURCE.APPLE_IAP
  if (src === FUNDING_SOURCE.GOOGLE_IAP) return FUNDING_SOURCE.GOOGLE_IAP
  if (src === FUNDING_SOURCE.ADMIN) return FUNDING_SOURCE.ADMIN
  return FUNDING_SOURCE.NICEPAY
}

export function fundingSourceLabel(src?: string | null): string {
  return FUNDING_SOURCE_LABEL[resolveFundingSource(src)]
}

export function fundingUsageLabel(src?: string | null, pointType?: string | null): string {
  if (pointType === 'ACCUMULATED') return ACCUMULATED_USAGE_LABEL
  return FUNDING_SOURCE_USAGE[resolveFundingSource(src)]
}

/**
 * 앱(WebView) 표시용 라벨 — 상수값(NICEPAY 등)은 불변, 화면 문자열만 심사 안전 카피.
 * Apple 3.1.1 / 3.1.3: PG·나이스페이·웹 충전 유도 문구 금지.
 */
export const FUNDING_SOURCE_LABEL_IN_APP: Record<FundingSource, string> = {
  NICEPAY: '',
  APPLE_IAP: '인앱결제',
  GOOGLE_IAP: '인앱결제',
  ADMIN: '관리자 지급',
}

export const FUNDING_SOURCE_USAGE_IN_APP: Record<FundingSource, string> = {
  NICEPAY: '사용 가능',
  APPLE_IAP: '스토어 환불',
  GOOGLE_IAP: '스토어 환불',
  ADMIN: '환불 불가',
}

/** 앱에서는 웹 PG/나이스페이 범례를 숨기고 인앱결제 안내만 */
export const FUNDING_SOURCE_LEGEND_IN_APP = [
  '인앱결제 충전분: 쇼핑몰 제외 사용 가능 · 환불은 스토어에서',
] as const

export function fundingSourceLabelForDisplay(src?: string | null, inApp = false): string {
  const resolved = resolveFundingSource(src)
  return inApp ? FUNDING_SOURCE_LABEL_IN_APP[resolved] : FUNDING_SOURCE_LABEL[resolved]
}

export function fundingUsageLabelForDisplay(src?: string | null, pointType?: string | null, inApp = false): string {
  if (pointType === 'ACCUMULATED') return ACCUMULATED_USAGE_LABEL
  const resolved = resolveFundingSource(src)
  return inApp ? FUNDING_SOURCE_USAGE_IN_APP[resolved] : FUNDING_SOURCE_USAGE[resolved]
}

export function fundingSourceLegend(inApp = false): readonly string[] {
  return inApp ? FUNDING_SOURCE_LEGEND_IN_APP : FUNDING_SOURCE_LEGEND
}

/**
 * 이 회원에게 <b>실제로 해당되는</b> 범례만 돌려준다.
 *
 * <p>종전에는 세 줄(웹 PG·인앱결제·관리자 지급)을 누구에게나 보여줬다. 그런데
 * <b>관리자 지급</b>은 회원이 스스로 만들 수 없는 재원이고, <b>인앱결제 충전분</b>도
 * 앱에서 충전한 적이 없으면 평생 0 이다. 해당 없는 설명이 잔액 카드를 채우면
 * 정작 읽어야 할 줄이 묻힌다(2026-09-11 사용자 지적).</p>
 *
 * <p>규칙 — 「가질 수 있는 것」과 「이미 가진 것」만 적는다.
 * 웹에서는 PG 로 충전할 수 있으니 그 줄은 항상 / 앱에서는 인앱결제로 충전하니 그 줄은 항상
 * (웹 PG 는 Apple 3.1.1 로 앱에서 감춘다) / 나머지는 <b>잔액이 있을 때만</b>.</p>
 *
 * @param b     잔액 분해(iapChargedBalance·adminChargedBalance). 없으면 «가질 수 있는 것» 만 남는다
 * @param inApp 앱(WebView) 여부
 */
export function fundingSourceLegendFor(
  b?: { iapChargedBalance?: number | null; adminChargedBalance?: number | null } | null,
  inApp = false,
): string[] {
  const iap = Number(b?.iapChargedBalance ?? 0)
  const admin = Number(b?.adminChargedBalance ?? 0)
  const lines: string[] = []
  if (inApp) {
    lines.push(FUNDING_SOURCE_LEGEND_IN_APP[0])
  } else {
    lines.push(FUNDING_SOURCE_LEGEND[0])                 // 웹 PG 충전 — 웹의 충전 수단
    if (iap > 0) lines.push(FUNDING_SOURCE_LEGEND[1])    // 인앱 충전분이 실제로 있을 때만
  }
  if (admin > 0) lines.push(FUNDING_SOURCE_LEGEND[2])    // 관리자 지급분이 실제로 있을 때만
  return lines
}

// ========================================
// 골총캐쉬 결제수단 (PAYMENT_METHOD)
// 이후 발생분. CMS 레거시 TRANSFER 는 BANK(계좌이체)와 동일 표시.
// ========================================
export const PAYMENT_METHOD = {
  CARD: 'CARD',
  BANK: 'BANK',
  IAP: 'IAP',
  ADMIN: 'ADMIN',
  REFUND: 'REFUND',
} as const
export type PaymentMethod = (typeof PAYMENT_METHOD)[keyof typeof PAYMENT_METHOD]

export const PAYMENT_METHOD_LABEL: Record<string, string> = {
  CARD: '카드결제',
  BANK: '계좌이체',
  TRANSFER: '계좌이체', // 레거시 저장값 — 일괄 UPDATE 하지 않음
  IAP: '인앱결제',
  ADMIN: '관리자',
  REFUND: '환불신청',
}
